"use client";

import { BASE_URL, PAGE_SIZE } from "@/constants";
import { useIntersectionObserver } from "@frend-digital/ui/hooks";
import { memo, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/Button";
import { ProductCard, ProductCardSkeleton } from "../../ProductCard";
import { ProductCardWithCampaigns } from "@/features/campaigns";
import { useSearchParams } from "next/navigation";
import { usePathname } from "@/i18n/routing";
import { Translate } from "@/components/Translate";

import { useProducts } from "../ListingContext";
import type { ProductCard as ProductCardType } from "@/lib/centra";
import { sizes } from "@/components/bloks/Asset";

type Props = {
	preview: boolean;
	showMore: boolean;
	setShowMore: React.Dispatch<React.SetStateAction<boolean>>;
};

const ProductListSection = memo(
	({
		products,
		priority: initialPriority,
		parentHref,
	}: { products: ProductCardType[]; priority?: boolean; parentHref: string }) => {
		return (
			<>
				{products.map((product, index) => {
					const priority = initialPriority && index < 4;
					return (
						<ProductCardWithCampaigns
							key={product.displayItemId}
							priority={priority}
							sizes={sizes(45, 25, 20)}
							product={product}
							parentHref={parentHref}
							showRatings={true}
						/>
					);
				})}
			</>
		);
	},
);

export const InfiniteProductList = ({ showMore, setShowMore }: Props) => {
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const showAsOnSale = false; //TODO fix
	const pageSearchParam = searchParams.get("page");
	const page = pageSearchParam ? Number.parseInt(pageSearchParam, 10) : null;

	const { query, parentHref, isLoading } = useProducts();

	const {
		data: result,
		fetchNextPage,
		isFetching,
		isPlaceholderData,
		hasNextPage,
		hasPreviousPage,
	} = query;

	// When the filters/sort change, the previous results are kept as placeholder data.
	// Show the skeleton in their place instead of below them, so the loading state is visible.
	const isRefreshing = isPlaceholderData && isFetching;

	const hasMore = hasNextPage;

	const data = result as typeof result | undefined;

	const { next, prev } = useMemo(() => {
		if (!data?.pageParams)
			return {
				prev: null,
				next: null,
			};
		const currentPage = page || data.pageParams[data.pageParams.length - 1];
		if (typeof currentPage !== "number")
			return {
				prev: null,
				next: null,
			};
		let next: string | null = null;
		let prev: string | null = null;

		if (hasPreviousPage) {
			const url = new URL(pathname, BASE_URL);
			url.searchParams.set("page", String(currentPage - 1));
			prev = url.toString();
		}
		if (hasNextPage) {
			const url = new URL(pathname, BASE_URL);
			url.searchParams.set("page", String(currentPage + 1));
			next = url.toString();
		}

		return {
			prev,
			next,
		};
	}, [hasNextPage, hasPreviousPage, page, pathname, data?.pageParams]);

	return (
		<>
			{prev && <link rel="prev" href={prev} />}
			{next && <link rel="next" href={next} />}
			{!isRefreshing &&
				data?.pages.map((item, idx) => (
					<ProductListSection
						// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
						key={idx}
						products={item.products}
						parentHref={parentHref}
						priority={idx === 0}
					/>
				))}
			{isLoading && <ListSkeleton />}
			{isFetching && <ListSkeleton />}
			{!showMore && hasMore && !isRefreshing && (
				<div
					style={{
						gridColumn: "1 / -1",
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Button
						onClick={() => {
							setShowMore(true);
							fetchNextPage();
						}}
					>
						<Translate>Show more</Translate>
					</Button>
				</div>
			)}
			{showMore && <Sentinel action={fetchNextPage} />}
		</>
	);
};

const ListSkeleton = () => {
	return (
		<>
			{Array.from({ length: PAGE_SIZE }, (v, k) => k).map((value, idx) => (
				<ProductCardSkeleton key={value} />
			))}
		</>
	);
};

const Sentinel = ({ action }: { action: () => void; inverted?: boolean }) => {
	const [ref, entry] = useIntersectionObserver<HTMLDivElement>();

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (entry?.isIntersecting) {
			action();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entry?.isIntersecting]);

	return (
		<span
			ref={ref}
			style={{
				height: 1,
				opacity: 0,
				gridColumn: "1 / -1",
				backgroundColor: "red",
				marginTop: "-400px",
			}}
		/>
	);
};
